import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { retargetClip, type Rig } from './rig';
import { retry } from '../engine/retry';

/**
 * Rocketbox clips retargeted onto a fitted MakeHuman rig. The source rest pose is a Rocketbox
 * avatar of the same sex in its bind pose; clips are shared by every Rocketbox avatar.
 */

const SOURCE = { m: 'Male_Adult_04', f: 'Business_Female_01' } as const;
type Sex = keyof typeof SOURCE;

const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
const base = `${import.meta.env.BASE_URL}people`;
const sources = new Map<Sex, Promise<THREE.Object3D>>();
const clips = new Map<string, Promise<THREE.AnimationClip>>();

export function source(sex: Sex) {
  let p = sources.get(sex);
  if (!p) sources.set(sex, (p = retry(() => loader.loadAsync(`${base}/avatars/${SOURCE[sex]}.glb`)).then((g) => g.scene)));
  return p;
}
export function clip(sex: Sex, name: string) {
  const key = `${sex}_${name}`;
  let p = clips.get(key);
  if (!p) clips.set(key, (p = retry(() => loader.loadAsync(`${base}/anims/${key}.glb`)).then((g) => { const c = g.animations[0]; c.name = key; return c; })));
  return p;
}

/**
 * How much longer this body's stride is than the source avatar's (hip height ratio): multiply
 * the source walk speed by it so the feet don't slide.
 */
export async function strideScale(rig: Rig, sex: Sex): Promise<number> {
  const src = await source(sex);
  src.updateMatrixWorld(true);
  let pelvis: THREE.Object3D | undefined;
  src.traverse((o) => { if (o.name === 'Bip01_Pelvis' || o.name === 'Bip01 Pelvis') pelvis = o; });
  const sy = pelvis?.getWorldPosition(new THREE.Vector3()).y ?? 0;
  return sy > 0 ? (rig.restWorldP.get('root')?.y ?? sy) / sy : 1;
}

/** one retarget at a time per source skeleton (each poses it, and yields to the game while it works) */
const turns = new WeakMap<THREE.Object3D, Promise<unknown>>();
function turn<T>(src: THREE.Object3D, job: () => Promise<T>): Promise<T> {
  const run = (turns.get(src) ?? Promise.resolve()).catch(() => {}).then(job);
  turns.set(src, run);
  return run;
}

/** Retarget the named clips (e.g. 'idle', 'walk') for this rig. Keys are `${sex}_${name}`. */
export async function retargetedClips(rig: Rig, sex: Sex, names: string[]): Promise<Map<string, THREE.AnimationClip>> {
  const src = await source(sex);
  const nodes = new Set<string>();
  src.traverse((o) => nodes.add(o.name));
  const out = new Map<string, THREE.AnimationClip>();
  for (const n of names) {
    const c = (await clip(sex, n)).clone();
    // clips include face/finger "nub" helpers the avatars don't have
    c.tracks = c.tracks.filter((t) => nodes.has(t.name.slice(0, t.name.lastIndexOf('.'))));
    out.set(`${sex}_${n}`, await turn(src, () => retargetClip(src, c, rig)));
  }
  return out;
}
